import { MapPin, Info } from 'lucide-react';

const UF_LOJA = 'CE';

// Texto por regime: o que muda no custo conforme a nota venha de fora ou de dentro do Ceará.
const AJUSTE_POR_REGIME = {
  foraDoCE: {
    presumido: [
      'A antecipação/DIFAL é paga na entrada, mas a Dantas Eletro (Lucro Presumido) recupera esse valor como crédito — por isso aparece só como informação e não soma no custo.',
      'O ICMS destacado na nota (4%, 7% ou 12%) e o ICMS do CT-e também viram crédito e são abatidos do custo, exceto nos itens com ST.',
    ],
    simples: [
      'No Simples Nacional não há crédito: a antecipação/DIFAL que a loja paga na entrada SOMA no custo do produto.',
      'O ICMS destacado na nota já está dentro do preço pago e não é abatido.',
    ],
  },
  dentroDoCE: {
    presumido: [
      'Não há antecipação nem diferença de alíquota: o fornecedor já cobrou o ICMS interno de 20%.',
      'A Dantas Eletro (Lucro Presumido) aproveita o ICMS destacado na nota como crédito — ele é abatido do custo, exceto nos itens com ST.',
    ],
    simples: [
      'Não há antecipação nem diferença de alíquota: o fornecedor já cobrou o ICMS interno de 20%.',
      'No Simples Nacional não há crédito: o ICMS da nota já está dentro do preço pago e não é abatido.',
    ],
  },
};

/**
 * Quadro destacado explicando, para leigo, como a origem da nota (fora ou dentro do CE)
 * e o regime da loja mudam o cálculo do custo.
 */
export default function ExplicacaoOrigem({ ufOrigem, regime, aliquotaInternaCE }) {
  const foraDoCE = Boolean(ufOrigem) && ufOrigem !== UF_LOJA;
  const ajustes = regime ? AJUSTE_POR_REGIME[foraDoCE ? 'foraDoCE' : 'dentroDoCE'][regime] : null;

  return (
    <div className={`rounded-xl border-2 p-5 ${foraDoCE ? 'border-orange-300 bg-orange-50' : 'border-emerald-300 bg-emerald-50'}`}>
      <div className="flex items-center gap-2 mb-2">
        <MapPin className={`w-5 h-5 ${foraDoCE ? 'text-orange-600' : 'text-emerald-600'}`} />
        <h3 className={`font-semibold ${foraDoCE ? 'text-orange-900' : 'text-emerald-900'}`}>
          {foraDoCE
            ? `Nota vinda de fora do Ceará (${ufOrigem} → CE)`
            : 'Nota de dentro do Ceará (CE → CE)'}
        </h3>
      </div>

      {foraDoCE ? (
        <div className="space-y-2 text-sm text-gray-800">
          <p>
            Em compra de outro estado, o fornecedor destaca o <strong>ICMS interestadual</strong>, que é menor:
            {' '}<strong>4%</strong> para produto importado, <strong>7%</strong> vindo do Sul/Sudeste (exceto ES) e
            {' '}<strong>12%</strong> dos demais estados.
          </p>
          <p>
            O Ceará cobra a diferença até a alíquota interna de <strong>{aliquotaInternaCE}%</strong> — é a
            {' '}<strong>antecipação / diferença de alíquota (DIFAL)</strong>, calculada assim:
          </p>
          <p className="font-mono text-xs bg-white/70 border border-orange-200 rounded-md px-3 py-2">
            (produto − desconto + despesas + IPI + frete) × (1 + MVA%) × {aliquotaInternaCE}% − ICMS que veio na nota
          </p>
          <p className="text-xs text-gray-600">
            Exemplo: base de R$ 1.000,00 vinda de SP (7%, ICMS de R$ 70,00) e sem MVA →
            {' '}R$ 1.000,00 × {aliquotaInternaCE}% − R$ 70,00 = <strong>R$ {(1000 * aliquotaInternaCE / 100 - 70).toFixed(2).replace('.', ',')}</strong> de antecipação.
            Com ST já retida na nota, não se calcula de novo.
          </p>
        </div>
      ) : (
        <p className="text-sm text-gray-800">
          Em compra dentro do estado, o fornecedor já destaca o <strong>ICMS interno de {aliquotaInternaCE}%</strong>.
          {' '}Não existe diferença de alíquota a pagar na entrada, então o custo é o valor da nota mais IPI, ST e frete.
        </p>
      )}

      <div className="mt-3 pt-3 border-t border-black/10">
        {ajustes ? (
          <ul className="space-y-1 text-sm text-gray-800">
            {ajustes.map(t => (
              <li key={t} className="flex items-start gap-2">
                <Info className="w-4 h-4 mt-0.5 flex-shrink-0 text-gray-500" />{t}
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-red-700">Escolha o regime da loja para ver como o custo é ajustado.</p>
        )}
      </div>
    </div>
  );
}
